import {
  AXIS_COLOR_ON_DARK,
  AXIS_COLOR_ON_LIGHT,
  BASE_RULER_FONT_SIZE,
  DARK_TRAINING_DOT_COLORS,
  DEFAULT_COLOR_ON_DARK,
  DEFAULT_COLOR_ON_LIGHT,
  getScaledLabelFont,
  GRID_COLOR_ON_DARK,
  GRID_COLOR_ON_LIGHT,
  INVISIBLE_COLOR,
  LIGHT_TRAINING_DOT_COLORS,
  PITCH_REPORT_FONT_SIZE,
  SVG_FALLBACK_CROSSHAIR_COLOR,
} from 'enums/canvas';
import {
  ARROW_UP_SVG,
  CROSS2_SVG,
  CROSS_SVG,
  CROSSHAIR_OUTLINE_SVG,
} from 'enums/svg-xmls';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { EllipseHelper } from 'lib_ts/classes/ellipse.helper';
import {
  FT_TO_INCHES,
  IN_TO_CM,
  RAD_FULL_ROTATION,
} from 'lib_ts/classes/math.utilities';
import { VISIBLE_STRIKEZONE } from 'lib_ts/classes/plate.helper';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { HitterSide } from 'lib_ts/enums/hitters.enums';
import { IEllipse } from 'lib_ts/interfaces/i-ellipses';
import { IHitter } from 'lib_ts/interfaces/i-hitter';
import { IPlateConfig } from 'lib_ts/interfaces/i-plate-config';
import { IStrikeZone } from 'lib_ts/interfaces/i-strike-zone';
import { DEFAULT_PLATE, IPlateLoc } from 'lib_ts/interfaces/pitches';
import { clamp } from 'lodash';

/** home plate is 17 inches wide */
const SZ_GUTTER_FT = 2 / 12;

/** minimum radius in ft for ellipse drawings */
const MIN_RADIUS_FT = 0.1;

/** only auto-adjust shots that land within the strike zone + a certain buffer around */
export const SZ_GREEN_BUFFER_FT = 7 / 12;

export const MIN_CONFIDENCE = 0.9;

export const MAX_SHOTS_USED = 10;

export const DOT_RGB_YELLOW = '255, 193, 6';
export const DOT_RGB_GREEN = '25, 135, 84';
export const DOT_RGB_BLUE = '49, 210, 242';

interface ICoord {
  x: number;
  y: number;
}

interface IConfig {
  strikeZone: IStrikeZone;
  isDark: boolean;
  ftToPx: number;
  minXFt: number;
  maxXFt: number;
  minYFt: number;
  maxYFt: number;
  widthScale: number;
  step?: number;
  /** multiplier for strikezone line thickness (default: 1) */
  szLineWidthScale?: number;
}

export class PlateCanvas {
  static makePortrait(strikeZone: IStrikeZone, isDark: boolean) {
    return new PlateCanvas({
      strikeZone: strikeZone,
      isDark: isDark,
      ftToPx: 300,
      minXFt: -1.5,
      maxXFt: 1.5,
      minYFt: 0,
      maxYFt: 4.5,
      widthScale: 3,
    });
  }

  static makeLandscape(strikeZone: IStrikeZone, isDark: boolean) {
    const minYFt = -0.5;
    const maxYFt = 5;

    // ensures landscape always ends up w/ 2.5 aspect ratio (overkill but ensures the drawing fills the view even on wide screens)
    const limitXFt = (2.5 * (maxYFt - minYFt)) / 2;

    return new PlateCanvas({
      strikeZone: strikeZone,
      isDark: isDark,
      ftToPx: 300,
      minXFt: -limitXFt,
      maxXFt: limitXFt,
      minYFt: minYFt,
      maxYFt: maxYFt,
      widthScale: 3,
    });
  }

  static makePresetTraining(
    strikeZone: IStrikeZone,
    isDark: boolean,
    aspectRatio: number
  ) {
    const deltaYFt = 30 / FT_TO_INCHES;

    const midYFt = 2.5;

    const minYFt = midYFt - deltaYFt;
    const maxYFt = midYFt + deltaYFt;

    return new PlateCanvas({
      strikeZone: strikeZone,
      isDark: isDark,
      ftToPx: 240,
      minXFt: -deltaYFt * aspectRatio,
      maxXFt: deltaYFt * aspectRatio,
      minYFt: minYFt,
      maxYFt: maxYFt,
      widthScale: 3,
    });
  }

  static makeWidget(strikeZone: IStrikeZone, isDark: boolean) {
    // Calculate strikezone dimensions
    const szHeightFt = strikeZone.top_ft - strikeZone.bottom_ft;
    const szCenterY = (strikeZone.top_ft + strikeZone.bottom_ft) / 2;

    // Add generous vertical padding (~60% of zone height on each side)
    // This allows balls outside the zone to be displayed
    const yPadding = szHeightFt * 0.6;

    return new PlateCanvas({
      strikeZone: strikeZone,
      isDark: isDark,
      ftToPx: 300,
      minXFt: strikeZone.left_ft - 0.7,
      maxXFt: strikeZone.right_ft + 0.7,
      minYFt: szCenterY - szHeightFt / 2 - yPadding,
      maxYFt: szCenterY + szHeightFt / 2 + yPadding,
      widthScale: 2,
      szLineWidthScale: 2,
    });
  }

  // used for detecting acceptability of shots during training, not for drawing anything
  static makeAbstract() {
    return new PlateCanvas({
      strikeZone: VISIBLE_STRIKEZONE,
      isDark: false,
      ftToPx: 100,
      minXFt: -1,
      maxXFt: 1,
      minYFt: -2,
      maxYFt: 6,
      widthScale: 1,
    });
  }

  // used for PP reports, 60" x 60" to match the break plot scale
  static makeAbstractSquare(isDark: boolean) {
    return new PlateCanvas({
      strikeZone: VISIBLE_STRIKEZONE,
      isDark: isDark,
      ftToPx: 240,
      minXFt: -2.5,
      maxXFt: 2.5,
      minYFt: VISIBLE_STRIKEZONE.plate_ft.plate_z - 2.5,
      maxYFt: VISIBLE_STRIKEZONE.plate_ft.plate_z + 2.5,
      widthScale: 3,
    });
  }

  getDefaultPlate() {
    const output: IPlateLoc = {
      plate_x: this.CONFIG.x.defaultFt,
      plate_z: this.CONFIG.y.defaultFt,
    };

    return output;
  }

  readonly CONFIG: IPlateConfig;

  private readonly SZ_PX: {
    top: number;
    bottom: number;
    left: number;
    right: number;
    middle_x: number;
    middle_y: number;
  };

  private readonly SZ_AUTO_ADJUST_PX: {
    top: number;
    bottom: number;
    left: number;
    right: number;
  };

  /** for gutter around strike zone */
  private readonly SZ_GUTTER_PX: {
    top: number;
    bottom: number;
    left: number;
    right: number;
  };

  private readonly HOME_PLATE_PX: {
    bottom: number;
    tip: number;
    left_line_inside: number;
    left_line_outside: number;
    right_line_inside: number;
    right_line_outside: number;
  };

  private readonly CONSTANTS_PX: {
    origin_x: number;
    origin_y: number;
  };

  /** multiplier for strikezone line thickness (default: 1) */
  private readonly szLineWidthScale: number;

  constructor(config: IConfig) {
    this.szLineWidthScale = config.szLineWidthScale ?? 1;
    const deltaXFt = config.maxXFt - config.minXFt;
    const deltaYFt = config.maxYFt - config.minYFt;

    this.CONFIG = {
      isDark: config.isDark,
      colors: {
        default: config.isDark ? DEFAULT_COLOR_ON_DARK : DEFAULT_COLOR_ON_LIGHT,
        axes: config.isDark ? AXIS_COLOR_ON_DARK : AXIS_COLOR_ON_LIGHT,
        grid: config.isDark ? GRID_COLOR_ON_DARK : GRID_COLOR_ON_LIGHT,
      },
      zone: config.strikeZone,
      canvas: {
        widthPx: deltaXFt * config.ftToPx,
        heightPx: deltaYFt * config.ftToPx,
        widthScale: config.widthScale,
        aspectRatio: (deltaXFt / deltaYFt).toFixed(1),
      },
      x: {
        minFt: config.minXFt,
        maxFt: config.maxXFt,
        defaultFt: 0,
        step: config.step ?? 0.00001,
      },
      y: {
        minFt: config.minYFt,
        maxFt: config.maxYFt,
        defaultFt: (config.strikeZone.top_ft + config.strikeZone.bottom_ft) / 2,
        step: config.step ?? 0.00001,
      },
    };

    /** must be after plate is defined */
    this.CONSTANTS_PX = {
      origin_x: this.ftToPxX(0),
      origin_y: this.ftToPxY(0),
    };

    this.SZ_PX = {
      top: this.ftToPxY(this.CONFIG.zone.top_ft),
      bottom: this.ftToPxY(this.CONFIG.zone.bottom_ft),
      left: this.ftToPxX(this.CONFIG.zone.left_ft),
      right: this.ftToPxX(this.CONFIG.zone.right_ft),
      middle_x: this.ftToPxX(0),
      middle_y: this.ftToPxY(
        (this.CONFIG.zone.top_ft + this.CONFIG.zone.bottom_ft) / 2
      ),
    };

    this.SZ_AUTO_ADJUST_PX = {
      top: this.ftToPxY(this.CONFIG.zone.top_ft + SZ_GREEN_BUFFER_FT),
      bottom: this.ftToPxY(this.CONFIG.zone.bottom_ft - SZ_GREEN_BUFFER_FT),
      left: this.ftToPxX(this.CONFIG.zone.left_ft - SZ_GREEN_BUFFER_FT),
      right: this.ftToPxX(this.CONFIG.zone.right_ft + SZ_GREEN_BUFFER_FT),
    };

    this.SZ_GUTTER_PX = {
      top: this.ftToPxY(this.CONFIG.zone.top_ft + SZ_GUTTER_FT),
      bottom: this.ftToPxY(this.CONFIG.zone.bottom_ft - SZ_GUTTER_FT),
      left: this.ftToPxX(this.CONFIG.zone.left_ft - SZ_GUTTER_FT),
      right: this.ftToPxX(this.CONFIG.zone.right_ft + SZ_GUTTER_FT),
    };

    this.HOME_PLATE_PX = {
      bottom: this.ftToPxY(-8.5 / 12 / 2),
      tip: this.ftToPxY((-8.5 * 2) / 12 / 2),
      left_line_inside: this.ftToPxX(-20 / 12 / 2),
      left_line_outside: this.ftToPxX(-36.25 / 12 / 2),
      right_line_inside: this.ftToPxX(20 / 12 / 2),
      right_line_outside: this.ftToPxX(36.25 / 12 / 2),
    };
  }

  private strikeZoneGridHeight(unit: 'px' | 'ft') {
    if (unit === 'px') {
      return (this.SZ_PX.bottom - this.SZ_PX.top) / 3;
    }

    if (unit === 'ft') {
      return (this.CONFIG.zone.top_ft - this.CONFIG.zone.bottom_ft) / 3;
    }

    return 0;
  }

  private strikeZoneGridWidth(unit: 'px' | 'ft') {
    if (unit === 'px') {
      return (this.SZ_PX.right - this.SZ_PX.left) / 3;
    }

    if (unit === 'ft') {
      return (this.CONFIG.zone.right_ft - this.CONFIG.zone.left_ft) / 3;
    }

    return 0;
  }

  ftToPxY(yFt: number) {
    /** convert y_ft into a px value that works for the canvas by translating so that y.min_ft is the same as 0 for the canvas */
    const total_ft = Math.abs(this.CONFIG.y.maxFt - this.CONFIG.y.minFt);
    const y_perc = (yFt - this.CONFIG.y.minFt) / total_ft;

    /** canvas y is inverted relative to slider */
    return this.CONFIG.canvas.heightPx * (1 - y_perc);
  }

  ftToPxX(xFt: number) {
    /** convert x_ft into a px value that works for the canvas by translating so that x.min_ft is the same as 0 for the canvas */
    const total_ft = Math.abs(this.CONFIG.x.maxFt - this.CONFIG.x.minFt);
    const x_perc = (xFt - this.CONFIG.x.minFt) / total_ft;
    return this.CONFIG.canvas.widthPx * x_perc;
  }

  drawPitchReportRulers(ctx: CanvasRenderingContext2D, tickIntervalIn: number) {
    ctx.font = getScaledLabelFont(
      this.CONFIG.canvas.widthScale,
      PITCH_REPORT_FONT_SIZE
    );
    ctx.lineWidth = this.getScaledWidth(1);
    ctx.fillStyle = this.CONFIG.colors.axes;

    const minX = this.ftToPxX(this.CONFIG.x.minFt);
    const maxX = this.ftToPxX(this.CONFIG.x.maxFt);

    const minY = this.ftToPxY(this.CONFIG.y.minFt);
    const maxY = this.ftToPxY(this.CONFIG.y.maxFt);

    ctx.strokeStyle = this.CONFIG.colors.grid;

    const xIntegerOptions = ArrayHelper.getIntegerOptions(
      Math.ceil(this.CONFIG.x.minFt * FT_TO_INCHES),
      Math.floor(this.CONFIG.x.maxFt * FT_TO_INCHES)
    );

    /** draw x grid marks */
    xIntegerOptions.forEach((o, i) => {
      const intValue = parseInt(o.value);

      if (intValue % tickIntervalIn !== 0) {
        return;
      }

      const x = this.ftToPxX(intValue / FT_TO_INCHES);

      /** don't draw first and last lines */
      if (i !== 0 && i !== xIntegerOptions.length - 1) {
        ctx.beginPath();
        ctx.moveTo(x, minY);
        ctx.lineTo(x, maxY);
        ctx.closePath();
        ctx.stroke();
      }

      /** draw x min and max units only (below ruler line) */
      if (i === 0) {
        ctx.fillText(
          `${o.value}"`,
          x + this.getScaledWidth(4),
          this.ftToPxY(this.CONFIG.y.defaultFt) + this.getScaledWidth(16)
        );
      } else if (i === xIntegerOptions.length - 1) {
        ctx.fillText(
          `${o.value}"`,
          // push the labels around a bit so they aren't cropped + account for -ve sign
          x - this.getScaledWidth(intValue > 0 ? 24 : -4),
          this.ftToPxY(this.CONFIG.y.defaultFt) + this.getScaledWidth(16)
        );
      }
    });

    const yIntegerOptions = ArrayHelper.getIntegerOptions(
      Math.ceil((this.CONFIG.y.minFt - this.CONFIG.y.defaultFt) * FT_TO_INCHES),
      Math.floor((this.CONFIG.y.maxFt - this.CONFIG.y.defaultFt) * FT_TO_INCHES)
    );

    /** draw y grid marks, centered on the middle of the SZ (not on y = 0) */
    yIntegerOptions.forEach((o, i) => {
      const intValue = parseInt(o.value);

      if (intValue % tickIntervalIn !== 0) {
        return;
      }

      // centers things vertically relative to the middle of the SZ
      const y = this.ftToPxY(this.CONFIG.y.defaultFt + intValue / FT_TO_INCHES);

      /** draw y min and max lines/units only (left of ruler line) */
      if (i === 0) {
        // i.e. for -24"
        ctx.fillText(
          `${intValue}"`,
          this.ftToPxX(0) - this.getScaledWidth(26),
          y - this.getScaledWidth(6)
        );
      } else if (i === xIntegerOptions.length - 1) {
        // i.e. for 24"
        ctx.fillText(
          `${intValue}"`,
          this.ftToPxX(0) - this.getScaledWidth(22),
          y + this.getScaledWidth(14)
        );
      } else {
        // don't draw first and last lines
        ctx.beginPath();
        ctx.moveTo(minX, y);
        ctx.lineTo(maxX, y);
        ctx.closePath();
        ctx.stroke();
      }
    });
  }

  drawPresetTrainingRulersInches(
    ctx: CanvasRenderingContext2D,
    tickInterval: number
  ) {
    ctx.font = getScaledLabelFont(
      this.CONFIG.canvas.widthScale,
      BASE_RULER_FONT_SIZE
    );
    ctx.lineWidth = this.getScaledWidth(1);

    const minX = this.ftToPxX(this.CONFIG.x.minFt);
    const maxX = this.ftToPxX(this.CONFIG.x.maxFt);

    const minY = this.ftToPxY(this.CONFIG.y.minFt);
    const maxY = this.ftToPxY(this.CONFIG.y.maxFt);

    const gradient = ctx.createLinearGradient(
      minX,
      this.ftToPxY(5),
      minX,
      this.ftToPxY(1)
    );

    const rgb = StringHelper.hexToRgb(this.CONFIG.colors.grid).join(',');

    // this does the fading, but it doesn't look very gradual w/ grey
    gradient.addColorStop(0, `rgba(${rgb},1)`);
    gradient.addColorStop(1, `rgba(${rgb},0)`);

    ctx.strokeStyle = gradient;

    const xLabelsAt = [-60, -30, 30, 60];

    const xIntegerOptions = ArrayHelper.getIntegerOptions(
      Math.ceil(this.CONFIG.x.minFt * FT_TO_INCHES),
      Math.floor(this.CONFIG.x.maxFt * FT_TO_INCHES)
    );

    /** draw x grid marks */
    xIntegerOptions.forEach((o, i) => {
      const intValue = parseInt(o.value);

      if (intValue % tickInterval !== 0) {
        return;
      }

      const x = this.ftToPxX(intValue / FT_TO_INCHES);

      /** don't draw first and last lines */
      if (i !== 0 && i !== xIntegerOptions.length - 1) {
        ctx.beginPath();
        ctx.moveTo(x, minY);
        ctx.lineTo(x, maxY);
        ctx.closePath();
        ctx.stroke();
      }

      /** draw x min and max units only (below ruler line) */
      if (xLabelsAt.includes(intValue)) {
        ctx.fillStyle = this.CONFIG.colors.axes;
        ctx.fillText(
          `${o.value}"`,
          // push the labels around a bit so they aren't cropped + account for -ve sign
          x - this.getScaledWidth(intValue > 0 ? 28 : -4),
          this.ftToPxY(this.CONFIG.y.defaultFt) + this.getScaledWidth(16)
        );
      }
    });

    const yIntegerOptions = ArrayHelper.getIntegerOptions(
      Math.ceil((this.CONFIG.y.minFt - this.CONFIG.y.defaultFt) * FT_TO_INCHES),
      Math.floor((this.CONFIG.y.maxFt - this.CONFIG.y.defaultFt) * FT_TO_INCHES)
    );

    /** draw y grid marks, centered on the middle of the SZ (not on y = 0) */
    yIntegerOptions.forEach((o, i) => {
      const intValue = parseInt(o.value);

      if (intValue % tickInterval !== 0) {
        return;
      }

      // centers things vertically relative to the middle of the SZ
      const y = this.ftToPxY(this.CONFIG.y.defaultFt + intValue / FT_TO_INCHES);

      // don't draw first and last lines
      if (i !== 0 && i !== yIntegerOptions.length - 1) {
        ctx.beginPath();
        ctx.moveTo(minX, y);
        ctx.lineTo(maxX, y);
        ctx.closePath();
        ctx.stroke();
      }
    });
  }

  drawPresetTrainingRulersCm(
    ctx: CanvasRenderingContext2D,
    tickInterval: number
  ) {
    ctx.font = getScaledLabelFont(
      this.CONFIG.canvas.widthScale,
      BASE_RULER_FONT_SIZE
    );
    ctx.lineWidth = this.getScaledWidth(1);

    const minX = this.ftToPxX(this.CONFIG.x.minFt);
    const maxX = this.ftToPxX(this.CONFIG.x.maxFt);

    const minY = this.ftToPxY(this.CONFIG.y.minFt);
    const maxY = this.ftToPxY(this.CONFIG.y.maxFt);

    const gradient = ctx.createLinearGradient(
      minX,
      this.ftToPxY(5),
      minX,
      this.ftToPxY(1)
    );

    const rgb = StringHelper.hexToRgb(this.CONFIG.colors.grid).join(',');

    // this does the fading, but it doesn't look very gradual w/ grey
    gradient.addColorStop(0, `rgba(${rgb},1)`);
    gradient.addColorStop(1, `rgba(${rgb},0)`);

    ctx.strokeStyle = gradient;

    const xLabelsAt = [-150, -75, 75, 150];

    const minXCm = Math.ceil(this.CONFIG.x.minFt * FT_TO_INCHES * IN_TO_CM);
    const maxXCm = Math.floor(this.CONFIG.x.maxFt * FT_TO_INCHES * IN_TO_CM);

    /** draw x grid marks */
    for (let value = minXCm; value <= maxXCm; value++) {
      if (value % tickInterval !== 0) {
        continue;
      }

      // convert from cm > inches > ft > pixels
      const x = this.ftToPxX(value / IN_TO_CM / FT_TO_INCHES);

      // draw vertical lines from top to bottom
      ctx.beginPath();
      ctx.moveTo(x, minY);
      ctx.lineTo(x, maxY);
      ctx.closePath();
      ctx.stroke();

      /** draw x min and max units only (below ruler line) */
      if (xLabelsAt.includes(value)) {
        ctx.fillStyle = this.CONFIG.colors.axes;
        ctx.fillText(
          `${value}cm`,
          // push the labels around a bit so they aren't cropped + account for -ve sign
          x - this.getScaledWidth(value > 0 ? 40 : 0),
          this.ftToPxY(this.CONFIG.y.defaultFt) + this.getScaledWidth(16)
        );
      }
    }

    const minYCm = Math.ceil(
      (this.CONFIG.y.minFt - this.CONFIG.y.defaultFt) * FT_TO_INCHES * IN_TO_CM
    );

    const maxYCm = Math.floor(
      (this.CONFIG.y.maxFt - this.CONFIG.y.defaultFt) * FT_TO_INCHES * IN_TO_CM
    );

    /** draw y grid marks, centered on the middle of the SZ (not on y = 0) */
    for (let value = minYCm; value <= maxYCm; value++) {
      if (value % tickInterval !== 0) {
        continue;
      }

      // centers things vertically relative to the middle of the SZ
      const y = this.ftToPxY(
        this.CONFIG.y.defaultFt + value / IN_TO_CM / FT_TO_INCHES
      );

      // draw horizontal lines from left to right
      ctx.beginPath();
      ctx.moveTo(minX, y);
      ctx.lineTo(maxX, y);
      ctx.closePath();
      ctx.stroke();
    }
  }

  private getScaledWidth(relValue: number) {
    return relValue * this.CONFIG.canvas.widthScale;
  }

  drawStrikeZone(
    ctx: CanvasRenderingContext2D,
    skipGrid?: boolean,
    szLineWidthScale?: number
  ) {
    const scale = szLineWidthScale ?? this.szLineWidthScale;

    if (!skipGrid) {
      this.drawStrikeZoneGrid(ctx, scale);
    }

    /** draw strike zone external box */
    ctx.lineWidth = this.getScaledWidth(2 * scale);
    ctx.strokeStyle = this.CONFIG.colors.default;

    ctx.strokeRect(
      this.SZ_PX.left,
      this.SZ_PX.top,
      this.SZ_PX.right - this.SZ_PX.left,
      this.SZ_PX.bottom - this.SZ_PX.top
    );
  }

  drawStrikeZoneSafetyRegion(ctx: CanvasRenderingContext2D, hitter: IHitter) {
    const REGION_BOTTOM_FT = -3;
    const REGION_HEIGHT_FT = 8;
    const REGION_WIDTH_FT = 4;
    const USE_GRADIENT = true;

    if (!hitter) {
      return;
    }

    if (!hitter.side) {
      return;
    }

    const xInsideFt = EllipseHelper.getSafeHitterInnerX(hitter.safety_buffer);
    if (xInsideFt === undefined) {
      return;
    }

    const isLeft = hitter.side === HitterSide.LHH;

    const leftX = this.ftToPxX(
      isLeft ? xInsideFt : -xInsideFt - REGION_WIDTH_FT
    );
    const rightX = this.ftToPxX(
      isLeft ? xInsideFt + REGION_WIDTH_FT : -xInsideFt
    );

    if (USE_GRADIENT) {
      // gradient
      const grd = ctx.createLinearGradient(
        isLeft ? leftX : rightX,
        0,
        isLeft ? rightX : leftX,
        0
      );

      // yellow
      grd.addColorStop(0, 'rgba(255, 193, 7, 0)');

      // red
      grd.addColorStop(0.5, 'rgba(245, 89, 89, 0.5)');

      ctx.fillStyle = grd;
    } else {
      // flat
      ctx.fillStyle = 'rgba(255, 193, 7, 0.2)';
    }

    ctx.fillRect(
      leftX,
      this.ftToPxY(REGION_BOTTOM_FT + REGION_HEIGHT_FT),
      rightX - leftX,
      this.ftToPxY(REGION_BOTTOM_FT) -
        this.ftToPxY(REGION_BOTTOM_FT + REGION_HEIGHT_FT)
    );
  }

  /** colored box around the strike zone, describes the area where the final training pitch needs to land to complete training */
  drawStrikeZoneAutoAdjustArea(
    ctx: CanvasRenderingContext2D,
    color = '#20573E'
  ) {
    ctx.lineWidth = this.getScaledWidth(2);
    ctx.strokeStyle = color;
    ctx.strokeRect(
      this.SZ_AUTO_ADJUST_PX.left,
      this.SZ_AUTO_ADJUST_PX.top,
      this.SZ_AUTO_ADJUST_PX.right - this.SZ_AUTO_ADJUST_PX.left,
      this.SZ_AUTO_ADJUST_PX.bottom - this.SZ_AUTO_ADJUST_PX.top
    );
  }

  private drawDetailedStrikeZone(ctx: CanvasRenderingContext2D) {
    this.drawStrikeZoneGrid(ctx);

    /** draw red strike zone external box */
    ctx.strokeStyle = '#FF0000';
    ctx.strokeRect(
      this.SZ_PX.left,
      this.SZ_PX.top,
      this.SZ_PX.right - this.SZ_PX.left,
      this.SZ_PX.bottom - this.SZ_PX.top
    );

    /** gutter (bigger) box */
    ctx.strokeRect(
      this.SZ_GUTTER_PX.left,
      this.SZ_GUTTER_PX.top,
      this.SZ_GUTTER_PX.right - this.SZ_GUTTER_PX.left,
      this.SZ_GUTTER_PX.bottom - this.SZ_GUTTER_PX.top
    );

    ctx.beginPath();

    /** top line separating corners */
    ctx.moveTo(this.SZ_PX.middle_x, this.SZ_GUTTER_PX.top);
    ctx.lineTo(this.SZ_PX.middle_x, this.SZ_PX.top);

    /** bottom line separating corners */
    ctx.moveTo(this.SZ_PX.middle_x, this.SZ_GUTTER_PX.bottom);
    ctx.lineTo(this.SZ_PX.middle_x, this.SZ_PX.bottom);

    /** left line separating corners */
    ctx.moveTo(this.SZ_GUTTER_PX.left, this.SZ_PX.middle_y);
    ctx.lineTo(this.SZ_PX.left, this.SZ_PX.middle_y);

    /** right line separating corners */
    ctx.moveTo(this.SZ_GUTTER_PX.right, this.SZ_PX.middle_y);
    ctx.lineTo(this.SZ_PX.right, this.SZ_PX.middle_y);

    ctx.closePath();
    ctx.stroke();
  }

  private drawStrikeZoneGrid(
    ctx: CanvasRenderingContext2D,
    szLineWidthScale?: number
  ) {
    const scale = szLineWidthScale ?? this.szLineWidthScale;

    ctx.lineWidth = this.getScaledWidth(2 * scale);
    ctx.strokeStyle = this.CONFIG.colors.default;

    ctx.beginPath();

    /** horizontal 1 */
    ctx.moveTo(
      this.SZ_PX.left,
      this.SZ_PX.top + this.strikeZoneGridHeight('px')
    );
    ctx.lineTo(
      this.SZ_PX.right,
      this.SZ_PX.top + this.strikeZoneGridHeight('px')
    );

    /** horizontal 2 */
    ctx.moveTo(
      this.SZ_PX.left,
      this.SZ_PX.top + this.strikeZoneGridHeight('px') * 2
    );
    ctx.lineTo(
      this.SZ_PX.right,
      this.SZ_PX.top + this.strikeZoneGridHeight('px') * 2
    );

    /** vertical 1 */
    ctx.moveTo(
      this.SZ_PX.left + this.strikeZoneGridWidth('px'),
      this.SZ_PX.top
    );
    ctx.lineTo(
      this.SZ_PX.left + this.strikeZoneGridWidth('px'),
      this.SZ_PX.bottom
    );

    /** vertical 2 */
    ctx.moveTo(
      this.SZ_PX.left + this.strikeZoneGridWidth('px') * 2,
      this.SZ_PX.top
    );
    ctx.lineTo(
      this.SZ_PX.left + this.strikeZoneGridWidth('px') * 2,
      this.SZ_PX.bottom
    );

    ctx.closePath();
    ctx.stroke();
  }

  private fillStrikeZoneCenterGrid(ctx: CanvasRenderingContext2D) {
    ctx.fillStyle = '#198754';

    /** horizontal 1 */
    ctx.fillRect(
      this.SZ_PX.left + this.strikeZoneGridWidth('px'),
      this.SZ_PX.top + this.strikeZoneGridHeight('px'),
      this.strikeZoneGridWidth('px'),
      this.strikeZoneGridHeight('px')
    );
  }

  // for testing where the viewable boundaries would lie
  private drawViewableBorder(ctx: CanvasRenderingContext2D) {
    ctx.strokeStyle = '#FF00FF';

    const wiggle = 0.001;

    const maxX = this.ftToPxX(this.CONFIG.x.maxFt - wiggle);
    const minX = this.ftToPxX(this.CONFIG.x.minFt + wiggle);
    const maxY = this.ftToPxY(this.CONFIG.y.maxFt - wiggle);
    const minY = this.ftToPxY(this.CONFIG.y.minFt + wiggle);

    const shape = this.getShape([
      { x: minX, y: minY },
      { x: minX, y: maxY },
      { x: maxX, y: maxY },
      { x: maxX, y: minY },
    ]);

    ctx.stroke(shape);
  }

  private getShape = (pts: ICoord[]) => {
    const output = new Path2D();

    pts.forEach((p, i) => {
      if (i === 0) {
        // first point defines the starting location
        output.moveTo(p.x, p.y);
        return;
      }

      // all other points are movements from the previous point
      output.lineTo(p.x, p.y);
    });

    output.closePath();

    return output;
  };

  // for rendering shots that fall outside the viewable area of the widget
  drawGround(ctx: CanvasRenderingContext2D) {
    const getGroundWidthFactor = () => {
      // sidebar: aspect ratio is 0.7
      // training: aspect ratio can be 2 (for wide view) and 1 (for square view)
      switch (parseFloat(this.CONFIG.canvas.aspectRatio)) {
        case 1:
        case 2: {
          // e.g. square view and wide view for training dialog (with and without breaks plot)
          return 1.55; // reduce by 0.05 after ABS plate moved to mid-plate
        }

        default: {
          // e.g. portrait for PL sidebar
          return 1.95; // reduce by 0.05 after ABS plate moved to mid-plate
        }
      }
    };

    // * for testing purposes only
    // ctx.beginPath();
    // ctx.strokeStyle = '#FF0000';
    // ctx.moveTo(this.ftToPxX(-SZ_WIDTH_FT / 2), this.ftToPxY(0));
    // ctx.lineTo(this.ftToPxX(SZ_WIDTH_FT / 2), this.ftToPxY(0));
    // ctx.closePath();
    // ctx.stroke();

    return new Promise<HTMLImageElement>((resolve) => {
      // ~magic~ number to make sure home plate is the same width as the strike zone
      const scale = this.getScaledWidth(getGroundWidthFactor());

      // based on the dimensions of the input SVG
      const widthPx = scale * 1160;
      const heightPx = scale * 166;

      const img = new Image(widthPx, heightPx);

      img.src = `/img/${
        this.CONFIG.isDark ? 'dark' : 'light'
      }/plate-batters-box.svg`;

      img.onload = () => {
        // ensures the image displays centered horizontally
        const x = this.ftToPxX(0) - widthPx / 2;

        // another ~magic~ number to get it looking natural
        const y = this.ftToPxY(2.75);

        ctx.drawImage(img, x, y, widthPx, heightPx);

        resolve(img);
      };
    });
  }

  // for rendering shots that fall outside the viewable area of the widget
  private async drawEdgeArrow(
    ctx: CanvasRenderingContext2D,
    config: {
      plate: IPlateLoc;
      size: number;
      color: string;
      pointing: 'u' | 'd' | 'l' | 'r';
      rotateDegrees: number;
    }
  ) {
    console.debug('drawEdgeArrow', config);

    const VIA_SHAPE = true;

    if (VIA_SHAPE) {
      const halfWidth = this.getScaledWidth(config.size * 0.004);
      const height = halfWidth * 2;
      const divet = height * 0.8;

      const pointsFt: ICoord[] = [];
      const bufferFt = 0.05;

      switch (config.pointing) {
        case 'u': {
          pointsFt.push(
            {
              // tip
              x: config.plate.plate_x,
              y: this.CONFIG.y.maxFt - bufferFt,
            },
            {
              // right
              x: config.plate.plate_x + halfWidth,
              y: this.CONFIG.y.maxFt - height - bufferFt,
            },
            {
              // divet
              x: config.plate.plate_x,
              y: this.CONFIG.y.maxFt - divet - bufferFt,
            },
            {
              // left
              x: config.plate.plate_x - halfWidth,
              y: this.CONFIG.y.maxFt - height - bufferFt,
            }
          );
          break;
        }

        case 'd': {
          pointsFt.push(
            {
              // tip
              x: config.plate.plate_x,
              y: this.CONFIG.y.minFt + bufferFt,
            },
            {
              // right
              x: config.plate.plate_x + halfWidth,
              y: this.CONFIG.y.minFt + height + bufferFt,
            },
            {
              // divet
              x: config.plate.plate_x,
              y: this.CONFIG.y.minFt + divet + bufferFt,
            },
            {
              // left
              x: config.plate.plate_x - halfWidth,
              y: this.CONFIG.y.minFt + height + bufferFt,
            }
          );
          break;
        }

        case 'l': {
          pointsFt.push(
            {
              // tip
              x: this.CONFIG.x.minFt + bufferFt,
              y: config.plate.plate_z,
            },
            {
              // right
              x: this.CONFIG.x.minFt + height + bufferFt,
              y: config.plate.plate_z - halfWidth,
            },
            {
              // divet
              x: this.CONFIG.x.minFt + divet + bufferFt,
              y: config.plate.plate_z,
            },
            {
              // left
              x: this.CONFIG.x.minFt + height + bufferFt,
              y: config.plate.plate_z + halfWidth,
            }
          );
          break;
        }

        case 'r': {
          pointsFt.push(
            {
              // tip
              x: this.CONFIG.x.maxFt - bufferFt,
              y: config.plate.plate_z,
            },
            {
              // right
              x: this.CONFIG.x.maxFt - height - bufferFt,
              y: config.plate.plate_z - halfWidth,
            },
            {
              // divet
              x: this.CONFIG.x.maxFt - divet - bufferFt,
              y: config.plate.plate_z,
            },
            {
              // left
              x: this.CONFIG.x.maxFt - height - bufferFt,
              y: config.plate.plate_z + halfWidth,
            }
          );
          break;
        }

        default: {
          break;
        }
      }

      const arrow = this.getShape(
        pointsFt.map((p) => ({
          x: this.ftToPxX(p.x),
          y: this.ftToPxY(p.y),
        }))
      );

      ctx.fillStyle = config.color ?? '#FF0000';
      ctx.fill(arrow);

      return;
    }

    return new Promise<HTMLImageElement>((resolve) => {
      const scaledSize = this.getScaledWidth(20);

      const img = new Image(scaledSize, scaledSize);

      img.onload = () => {
        const wiggle = 0.1;

        const x = this.ftToPxX(
          clamp(
            config.plate.plate_x,
            this.CONFIG.x.minFt + wiggle,
            this.CONFIG.x.maxFt - wiggle
          )
        );

        const y = this.ftToPxY(
          clamp(
            config.plate.plate_z,
            this.CONFIG.y.minFt + wiggle,
            this.CONFIG.y.maxFt - wiggle
          )
        );

        // rescale the image to match target size
        ctx.drawImage(img, x, y, scaledSize, scaledSize);

        console.debug({
          event: 'arrow drawn',
          x,
          y,
        });

        resolve(img);
      };

      const svgXml = ARROW_UP_SVG.replaceAll(
        'currentColor',
        // default is to replace the white with green
        config.color
      );

      // ? encodeURIComponent is necessary to safely handle special chars, e.g. in hex-style color codes
      img.src =
        'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgXml);
    });
  }

  async drawDot(
    ctx: CanvasRenderingContext2D,
    config: {
      plate: IPlateLoc;
      color: string;
      size: number;
      labelText?: string;
    }
  ) {
    if (config.plate.plate_x < this.CONFIG.x.minFt) {
      console.debug('off screen left', config.plate);
      return await this.drawEdgeArrow(ctx, {
        plate: config.plate,
        size: config.size,
        pointing: 'l',
        color: config.color,
        rotateDegrees: -90,
      });
    }

    if (config.plate.plate_x > this.CONFIG.x.maxFt) {
      console.debug('off screen right', config.plate);
      return await this.drawEdgeArrow(ctx, {
        plate: config.plate,
        size: config.size,
        pointing: 'r',
        color: config.color,
        rotateDegrees: 90,
      });
    }

    if (config.plate.plate_z < this.CONFIG.y.minFt) {
      console.debug('off screen below', config.plate);
      return await this.drawEdgeArrow(ctx, {
        plate: config.plate,
        size: config.size,
        pointing: 'd',
        color: config.color,
        rotateDegrees: 180,
      });
    }

    if (config.plate.plate_z > this.CONFIG.y.maxFt) {
      console.debug('off screen above', config.plate);
      return await this.drawEdgeArrow(ctx, {
        plate: config.plate,
        size: config.size,
        pointing: 'u',
        color: config.color,
        rotateDegrees: 0,
      });
    }

    const scaledSize = this.getScaledWidth(config.size);

    ctx.strokeStyle = INVISIBLE_COLOR;
    ctx.fillStyle = config.color;
    ctx.beginPath();

    const x = this.ftToPxX(config.plate.plate_x);
    const y = this.ftToPxY(config.plate.plate_z);

    ctx.ellipse(x, y, scaledSize, scaledSize, 0, 0, RAD_FULL_ROTATION);
    ctx.fill();
    ctx.stroke();

    // console.debug({
    //   event: 'shot drawn',
    //   x,
    //   y,
    // });

    if (config.labelText) {
      ctx.font = getScaledLabelFont(this.CONFIG.canvas.widthScale);
      ctx.fillText(
        config.labelText,
        this.ftToPxX(config.plate.plate_x) + this.getScaledWidth(15),
        this.ftToPxY(config.plate.plate_z) + scaledSize / 2
      );
    }
  }

  drawTarget(
    ctx: CanvasRenderingContext2D,
    config: {
      as: 'cross' | 'crosshairs';
      target: IPlateLoc;
      width_px: number;
      color?: string;
      beforeDraw?: () => void;
    }
  ) {
    return new Promise<HTMLImageElement>((resolve) => {
      const scaledSize = this.getScaledWidth(config.width_px);

      const img = new Image(scaledSize, scaledSize);

      const svgXml = (config.as === 'cross' ? CROSS_SVG : CROSSHAIR_OUTLINE_SVG)
        .replaceAll(
          'currentColor',
          // default is to replace the white with red
          config.color ?? SVG_FALLBACK_CROSSHAIR_COLOR
        )
        .replaceAll('outlineColor', this.CONFIG.isDark ? '#1F2123' : '#FFFFFF');

      img.onload = () => {
        const x = this.ftToPxX(config.target.plate_x);
        const y = this.ftToPxY(config.target.plate_z);
        const half = scaledSize / 2;

        // img.onload is async, so we may want to do something right before drawing (e.g. clear canvas)
        if (config.beforeDraw) {
          config.beforeDraw();
        }

        // rescale the image to match target size
        ctx.drawImage(img, x - half, y - half, scaledSize, scaledSize);

        resolve(img);
      };

      // ? encodeURIComponent is necessary to safely handle special chars, e.g. in hex-style color codes
      img.src =
        'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgXml);
    });
  }

  async drawOutcome(
    ctx: CanvasRenderingContext2D,
    plate: IPlateLoc,
    isStrike: boolean
  ): Promise<void> {
    const arrowSize = this.getScaledWidth(4);
    const arrowColor = '#0090FF';

    if (plate.plate_x < this.CONFIG.x.minFt) {
      console.debug('off screen left', plate);
      await this.drawEdgeArrow(ctx, {
        plate: plate,
        size: arrowSize,
        pointing: 'l',
        color: arrowColor,
        rotateDegrees: -90,
      });
      return;
    }

    if (plate.plate_x > this.CONFIG.x.maxFt) {
      console.debug('off screen right', plate);
      await this.drawEdgeArrow(ctx, {
        plate: plate,
        size: arrowSize,
        pointing: 'r',
        color: arrowColor,
        rotateDegrees: 90,
      });
      return;
    }

    if (plate.plate_z < this.CONFIG.y.minFt) {
      console.debug('off screen below', plate);
      await this.drawEdgeArrow(ctx, {
        plate: plate,
        size: arrowSize,
        pointing: 'd',
        color: arrowColor,
        rotateDegrees: 180,
      });
      return;
    }

    if (plate.plate_z > this.CONFIG.y.maxFt) {
      console.debug('off screen above', plate);
      await this.drawEdgeArrow(ctx, {
        plate: plate,
        size: arrowSize,
        pointing: 'u',
        color: arrowColor,
        rotateDegrees: 0,
      });
      return;
    }

    return new Promise((resolve) => {
      const scaledSize = this.getScaledWidth(40);

      const img = new Image(scaledSize, scaledSize);

      img.src = isStrike
        ? '/img/strike-location.svg'
        : '/img/ball-location.svg';

      img.onload = () => {
        const x = this.ftToPxX(plate.plate_x);
        const y = this.ftToPxY(plate.plate_z);
        const half = scaledSize / 2;

        // rescale the image to match target size
        ctx.drawImage(img, x - half, y - half, scaledSize, scaledSize);

        resolve();
      };
    });
  }

  drawCheckCircled(
    ctx: CanvasRenderingContext2D,
    config: {
      mode: 'filled' | 'outlined';
      value: IPlateLoc;
      width_px: number;
    }
  ): Promise<void> {
    return new Promise((resolve) => {
      const scaledSize = this.getScaledWidth(config.width_px);

      const img = new Image(scaledSize, scaledSize);

      img.src =
        config.mode === 'filled'
          ? '/img/check-circled-filled.svg'
          : '/img/check-circled-outlined.svg';

      img.onload = () => {
        const x = this.ftToPxX(config.value.plate_x);
        const y = this.ftToPxY(config.value.plate_z);
        const half = scaledSize / 2;

        // rescale the image to match target size
        ctx.drawImage(img, x - half, y - half, scaledSize, scaledSize);

        resolve();
      };
    });
  }

  drawCrossmark(ctx: CanvasRenderingContext2D): Promise<void> {
    return new Promise((resolve) => {
      const scaledSize = this.getScaledWidth(32);

      const img = new Image(scaledSize, scaledSize);

      img.src = '/img/cross-circled-filled.svg';

      img.onload = () => {
        const x = this.ftToPxX(DEFAULT_PLATE.plate_x);
        const y = this.ftToPxY(DEFAULT_PLATE.plate_z);
        const half = scaledSize / 2;

        // rescale the image to match target size
        ctx.drawImage(img, x - half, y - half, scaledSize, scaledSize);

        resolve();
      };
    });
  }

  drawX(
    ctx: CanvasRenderingContext2D,
    config: {
      target: IPlateLoc;
      width_px: number;
      color?: string;
    }
  ): Promise<void> {
    return new Promise((resolve) => {
      const scaledSize = this.getScaledWidth(config.width_px);

      const img = new Image(scaledSize, scaledSize);

      const svgXml = CROSS2_SVG.replaceAll(
        'currentColor',
        // default is to replace the white with red
        config.color ?? '#FF5361'
      );

      // ? encodeURIComponent is necessary to safely handle special chars, e.g. in hex-style color codes
      img.src =
        'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgXml);

      img.onload = () => {
        const x = this.ftToPxX(config.target.plate_x);
        const y = this.ftToPxY(config.target.plate_z);
        const half = scaledSize / 2;

        // rescale the image to match target size
        ctx.drawImage(img, x - half, y - half, scaledSize, scaledSize);

        resolve();
      };
    });
  }

  drawTrainingDot(
    ctx: CanvasRenderingContext2D,
    config: {
      plate: IPlateLoc;
      isNew: boolean;
      size: number;
      index: number;
      label?: string;
    }
  ) {
    if (
      config.plate.plate_x < this.CONFIG.x.minFt ||
      config.plate.plate_x > this.CONFIG.x.maxFt
    ) {
      return;
    }

    if (
      config.plate.plate_z < this.CONFIG.y.minFt ||
      config.plate.plate_z > this.CONFIG.y.maxFt
    ) {
      return;
    }

    const scaledSize = this.getScaledWidth(config.size);

    const dots = this.CONFIG.isDark
      ? DARK_TRAINING_DOT_COLORS
      : LIGHT_TRAINING_DOT_COLORS;

    const colors = config.isNew ? dots.new : dots.old;

    ctx.lineWidth = this.getScaledWidth(2);
    ctx.fillStyle = colors.background;
    ctx.strokeStyle = colors.border;

    ctx.beginPath();
    ctx.ellipse(
      this.ftToPxX(config.plate.plate_x),
      this.ftToPxY(config.plate.plate_z),
      scaledSize,
      scaledSize,
      0,
      0,
      RAD_FULL_ROTATION
    );
    ctx.fill();
    ctx.stroke();

    ctx.font = getScaledLabelFont(this.CONFIG.canvas.widthScale);
    ctx.fillStyle = colors.label;

    ctx.fillText(
      // inside the circle
      config.index.toString(),
      this.ftToPxX(config.plate.plate_x) -
        scaledSize / 2 +
        this.getScaledWidth(1),
      this.ftToPxY(config.plate.plate_z) + scaledSize / 2
    );

    if (config.label) {
      // off to the side
      ctx.fillText(
        config.label,
        this.ftToPxX(config.plate.plate_x) + this.getScaledWidth(15),
        this.ftToPxY(config.plate.plate_z) + scaledSize / 2
      );
    }
  }

  drawThreshold(
    ctx: CanvasRenderingContext2D,
    config: {
      target: IPlateLoc;
      radiusIn: number;
      fillColor: string;
      borderColor: string;
    }
  ) {
    const radiusPx =
      this.ftToPxX(config.radiusIn / FT_TO_INCHES) - this.ftToPxX(0);

    ctx.fillStyle = config.fillColor;
    ctx.strokeStyle = config.borderColor;

    ctx.beginPath();
    ctx.lineWidth = this.getScaledWidth(1); // dot border thickness
    ctx.ellipse(
      this.ftToPxX(config.target.plate_x),
      this.ftToPxY(config.target.plate_z),
      radiusPx,
      radiusPx,
      0,
      0,
      RAD_FULL_ROTATION
    );
    ctx.fill();
    ctx.stroke();
  }

  drawActual(
    ctx: CanvasRenderingContext2D,
    config: {
      value: IPlateLoc;
      size: number;
      fillColor: string;
      borderColor: string;
    }
  ) {
    ctx.fillStyle = config.fillColor;
    ctx.strokeStyle = config.borderColor;

    const scaledSize = this.getScaledWidth(config.size);

    // draw the dot for the actual position
    ctx.beginPath();

    // solid borders around dots
    ctx.lineWidth = this.getScaledWidth(1); // dot border thickness
    ctx.setLineDash([]);

    ctx.ellipse(
      this.ftToPxX(config.value.plate_x),
      this.ftToPxY(config.value.plate_z),
      scaledSize,
      scaledSize,
      0,
      0,
      RAD_FULL_ROTATION
    );
    ctx.fill();
    ctx.stroke();
    ctx.closePath();
  }

  // uses a smaller font size, only used for report plot
  drawLabel(
    ctx: CanvasRenderingContext2D,
    config: {
      value: IPlateLoc;
      target: IPlateLoc;
      fontColor: string;
    }
  ) {
    const roundTo1 = (v: number) => Math.round(v * 10) / 10;

    // label the point
    const delta_x_in = roundTo1(
      (config.value.plate_x - config.target.plate_x) * FT_TO_INCHES
    );

    const delta_z_in = roundTo1(
      (config.value.plate_z - config.target.plate_z) * FT_TO_INCHES
    );

    const formatLabel = (v: number) =>
      `${v > 0 ? '+' : v < 0 ? '-' : ''}${Math.abs(v).toFixed(1)}`;

    ctx.font = getScaledLabelFont(
      this.CONFIG.canvas.widthScale,
      PITCH_REPORT_FONT_SIZE
    );
    ctx.fillStyle = config.fontColor;

    ctx.fillText(
      `${formatLabel(delta_x_in)}", ${formatLabel(delta_z_in)}"`,
      this.ftToPxX(config.value.plate_x) + 16,
      this.ftToPxY(config.value.plate_z) + 16
    );
  }

  drawLine(
    ctx: CanvasRenderingContext2D,
    config: {
      from: IPlateLoc;
      to: IPlateLoc;
      lineColor: string;
    }
  ) {
    ctx.strokeStyle = config.lineColor;
    ctx.lineWidth = this.getScaledWidth(1); // line thickness

    ctx.beginPath();
    ctx.setLineDash([8, 4]);
    ctx.moveTo(
      this.ftToPxX(config.from.plate_x),
      this.ftToPxY(config.from.plate_z)
    );
    ctx.lineTo(
      this.ftToPxX(config.to.plate_x),
      this.ftToPxY(config.to.plate_z)
    );
    ctx.closePath();
    ctx.stroke();

    // reset the line dash style for anything else after
    ctx.setLineDash([]);
  }

  drawEllipse(
    ctx: CanvasRenderingContext2D,
    config: {
      plate: IPlateLoc;
      fillStyle: string;
      ellipse: IEllipse;
      scaling_factor: number;
      angle_radians: number;
      bounds?: boolean;
      // like '#FF0000'
      borderColor?: string;
      drawBorder?: boolean;
    }
  ) {
    ctx.strokeStyle = config.drawBorder
      ? (config.borderColor ?? '#FF0000')
      : INVISIBLE_COLOR;

    // Impose min radius in ft ot prevent "straight line" ellipses
    // Will be replaced with prior ellipse trade off eventually
    const minRadiusFtSquared = Math.pow(MIN_RADIUS_FT, 2);
    const majorRadiusFtSquared = clamp(
      config.ellipse.majorRadius,
      minRadiusFtSquared,
      Infinity
    );
    const minorRadiusFtSquared = clamp(
      config.ellipse.minorRadius,
      minRadiusFtSquared,
      Infinity
    );

    const radiusX =
      (this.CONFIG.canvas.widthPx / 2 / this.CONFIG.x.maxFt) *
      Math.sqrt(majorRadiusFtSquared * config.scaling_factor);

    const radiusY =
      (this.CONFIG.canvas.heightPx /
        (this.CONFIG.y.maxFt - this.CONFIG.y.minFt)) *
      Math.sqrt(minorRadiusFtSquared * config.scaling_factor);

    const cx = this.ftToPxX(config.plate.plate_x);
    const cy = this.ftToPxY(config.plate.plate_z);

    /** draw the (filled) ellipse using a radial gradient that fades from the
     * provided fillStyle at the center to transparent at the edge. The
     * gradient is rendered via a transformed unit circle so the fade follows
     * the ellipse's shape and rotation. */
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(config.angle_radians);
    ctx.scale(radiusX, radiusY);

    const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, 1);
    gradient.addColorStop(0, config.fillStyle);
    gradient.addColorStop(0.25, config.fillStyle); // don't start fading until partway towards the edge
    gradient.addColorStop(1, 'transparent');

    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(0, 0, 1, 0, RAD_FULL_ROTATION);
    ctx.fill();
    ctx.restore();

    /** stroke the border in untransformed coordinates so line width is correct */
    ctx.beginPath();
    ctx.ellipse(
      cx, // x position
      cy, // y position
      radiusX,
      radiusY,
      config.angle_radians, // rotation
      0, // start angle

      /** end angle */
      RAD_FULL_ROTATION
    );
    ctx.stroke();

    if (config.bounds) {
      const limits = EllipseHelper.getEllipseLimits(
        config.ellipse,
        config.scaling_factor
      );

      const ellipse_bound_px = {
        top: this.ftToPxY(config.plate.plate_z + limits.absMaxY),
        bottom: this.ftToPxY(config.plate.plate_z - limits.absMaxY),
        left: this.ftToPxX(config.plate.plate_x - limits.absMaxX),
        right: this.ftToPxX(config.plate.plate_x + limits.absMaxX),
      };

      /** draw bounding box */
      ctx.lineWidth = this.getScaledWidth(2);
      ctx.strokeStyle = this.CONFIG.colors.default;
      ctx.strokeRect(
        ellipse_bound_px.left,
        ellipse_bound_px.top,
        ellipse_bound_px.right - ellipse_bound_px.left,
        ellipse_bound_px.bottom - ellipse_bound_px.top
      );
    }
  }

  getConfig() {
    return {
      CONFIG: this.CONFIG,
      SZ_PX: this.SZ_PX,
      SZ_GUTTER_PX: this.SZ_GUTTER_PX,
      SZ_AUTO_ADJUST_PX: this.SZ_AUTO_ADJUST_PX,
      HOME_PLATE_PX: this.HOME_PLATE_PX,
    };
  }
}
